import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
  setDoc,
  writeBatch,
} from 'firebase/firestore';
import { DB } from 'src/contexts/FirebaseContext';

// Import types
import {
  Engineer,
  EngineerAvailability,
  CreateEngineerRequest,
  UpdateEngineerRequest,
  EngineerResponse,
  EngineersResponse,
  EngineerFilters,
  CreateEngineerAvailabilityRequest,
  UpdateEngineerAvailabilityRequest,
  EngineerAvailabilityResponse,
  WeeklyScheduleDocument,
  UpsertWeekScheduleRequest,
  DeleteWeekScheduleRequest,
  GetWeekScheduleRequest,
  ListWeekSchedulesRequest,
} from 'src/@types/enhanced_organization';

// Helper function to convert Firestore timestamps to dates
const convertTimestamps = (data: any): any => {
  if (!data) return data;
  const converted = { ...data };
  if (converted.createdAt instanceof Timestamp) {
    converted.createdAt = converted.createdAt.toDate();
  }
  if (converted.updatedAt instanceof Timestamp) {
    converted.updatedAt = converted.updatedAt.toDate();
  }
  return converted;
};

// Helper function to calculate week end date from start date (Monday to Sunday)
const getWeekEndDate = (weekStartDate: string): string => {
  const startDate = new Date(weekStartDate);
  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + 6); // Add 6 days to get Sunday
  return endDate.toISOString().split('T')[0];
};

// ==================== ENGINEERS API ====================

export const engineersApi = createApi({
  reducerPath: 'engineersApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['Engineer', 'Engineers', 'EngineerAvailability'],
  endpoints: (builder) => ({

    // ==================== ENGINEER CRUD ====================

    /**
     * Get all engineers for an organization
     */
    getEngineersByOrganization: builder.query<EngineersResponse, string>({
      async queryFn(organizationId) {
        try {
          const engineersRef = collection(DB, 'organisers', organizationId, 'engineers');
          const q = query(
            engineersRef,
            where('isDeleted', '==', false),
            orderBy('name', 'asc')
          );

          const snapshot = await getDocs(q);
          const engineers: Engineer[] = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...convertTimestamps(doc.data()),
          })) as Engineer[];

          return { data: { engineers, total: engineers.length } };
        } catch (error: any) {
          console.error('Error fetching engineers:', error);
          return { error: { status: 'CUSTOM_ERROR', error: error.message } };
        }
      },
      providesTags: ['Engineers'],
    }),

    /**
     * Get a single engineer by ID
     */
    getEngineer: builder.query<EngineerResponse, { organizationId: string; engineerId: string }>({
      async queryFn({ organizationId, engineerId }) {
        try {
          const engineerRef = doc(DB, 'organisers', organizationId, 'engineers', engineerId);
          const engineerDoc = await getDoc(engineerRef);

          if (!engineerDoc.exists()) {
            return { error: { status: 'NOT_FOUND', error: 'Engineer not found' } };
          }

          const engineer: Engineer = {
            id: engineerDoc.id,
            ...convertTimestamps(engineerDoc.data()),
          } as Engineer;

          return { data: { engineer } };
        } catch (error: any) {
          console.error('Error fetching engineer:', error);
          return { error: { status: 'CUSTOM_ERROR', error: error.message } };
        }
      },
      providesTags: (result, error, { engineerId }) => [{ type: 'Engineer', id: engineerId }],
    }),

    /**
     * Create a new engineer
     */
    createEngineer: builder.mutation<EngineerResponse, CreateEngineerRequest>({
      async queryFn(engineerData) {
        try {
          const { organizationId, ...data } = engineerData;
          const engineersRef = collection(DB, 'organisers', organizationId, 'engineers');

          const newEngineer = {
            ...data,
            organizationId,
            isAvailable: data.isAvailable ?? true,
            isDeleted: false,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          };

          const docRef = await addDoc(engineersRef, newEngineer);
          const engineerDoc = await getDoc(docRef);

          const engineer: Engineer = {
            id: docRef.id,
            ...convertTimestamps(engineerDoc.data()),
          } as Engineer;

          return { data: { engineer } };
        } catch (error: any) {
          console.error('Error creating engineer:', error);
          return { error: { status: 'CUSTOM_ERROR', error: error.message } };
        }
      },
      invalidatesTags: ['Engineers'],
    }),

    /**
     * Update an existing engineer
     */
    updateEngineer: builder.mutation<EngineerResponse, UpdateEngineerRequest & { organizationId: string }>({
      async queryFn({ id, organizationId, ...updates }) {
        try {
          const engineerRef = doc(DB, 'organisers', organizationId, 'engineers', id);

          const updateData = {
            ...updates,
            updatedAt: serverTimestamp(),
          };

          await updateDoc(engineerRef, updateData);
          const engineerDoc = await getDoc(engineerRef);

          const engineer: Engineer = {
            id: engineerDoc.id,
            ...convertTimestamps(engineerDoc.data()),
          } as Engineer;

          return { data: { engineer } };
        } catch (error: any) {
          console.error('Error updating engineer:', error);
          return { error: { status: 'CUSTOM_ERROR', error: error.message } };
        }
      },
      invalidatesTags: (result, error, { id }) => [
        { type: 'Engineer', id },
        'Engineers',
      ],
    }),

    /**
     * Soft delete an engineer
     */
    deleteEngineer: builder.mutation<void, { organizationId: string; engineerId: string }>({
      async queryFn({ organizationId, engineerId }) {
        try {
          const engineerRef = doc(DB, 'organisers', organizationId, 'engineers', engineerId);
          await updateDoc(engineerRef, {
            isDeleted: true,
            isActive: false,
            updatedAt: serverTimestamp(),
          });

          return { data: undefined };
        } catch (error: any) {
          console.error('Error deleting engineer:', error);
          return { error: { status: 'CUSTOM_ERROR', error: error.message } };
        }
      },
      invalidatesTags: (result, error, { engineerId }) => [
        { type: 'Engineer', id: engineerId },
        'Engineers',
      ],
    }),

    /**
     * Toggle engineer active status
     */
    toggleEngineerStatus: builder.mutation<EngineerResponse, { organizationId: string; engineerId: string; isActive: boolean }>({
      async queryFn({ organizationId, engineerId, isActive }) {
        try {
          const engineerRef = doc(DB, 'organisers', organizationId, 'engineers', engineerId);
          await updateDoc(engineerRef, {
            isActive,
            updatedAt: serverTimestamp(),
          });

          const engineerDoc = await getDoc(engineerRef);
          const engineer: Engineer = {
            id: engineerDoc.id,
            ...convertTimestamps(engineerDoc.data()),
          } as Engineer;

          return { data: { engineer } };
        } catch (error: any) {
          console.error('Error toggling engineer status:', error);
          return { error: { status: 'CUSTOM_ERROR', error: error.message } };
        }
      },
      invalidatesTags: (result, error, { engineerId }) => [
        { type: 'Engineer', id: engineerId },
        'Engineers',
      ],
    }),

    /**
     * Toggle engineer availability
     */
    toggleEngineerAvailability: builder.mutation<EngineerResponse, { organizationId: string; engineerId: string; isAvailable: boolean }>({
      async queryFn({ organizationId, engineerId, isAvailable }) {
        try {
          const engineerRef = doc(DB, 'organisers', organizationId, 'engineers', engineerId);
          await updateDoc(engineerRef, {
            isAvailable,
            updatedAt: serverTimestamp(),
          });

          const engineerDoc = await getDoc(engineerRef);
          const engineer: Engineer = {
            id: engineerDoc.id,
            ...convertTimestamps(engineerDoc.data()),
          } as Engineer;

          return { data: { engineer } };
        } catch (error: any) {
          console.error('Error toggling engineer availability:', error);
          return { error: { status: 'CUSTOM_ERROR', error: error.message } };
        }
      },
      invalidatesTags: (result, error, { engineerId }) => [
        { type: 'Engineer', id: engineerId },
        'Engineers',
      ],
    }),

    // ==================== ENGINEER AVAILABILITY ====================

    /**
     * Get engineer availability settings
     */
    getEngineerAvailability: builder.query<EngineerAvailabilityResponse, { organizationId: string; engineerId: string }>({
      async queryFn({ organizationId, engineerId }) {
        try {
          const availabilityRef = doc(
            DB,
            'organisers',
            organizationId,
            'engineers',
            engineerId,
            'availability_settings',
            'default'
          );
          const availabilityDoc = await getDoc(availabilityRef);

          if (!availabilityDoc.exists()) {
            // Return default availability if none exists
            const availability: EngineerAvailability = {
              id: 'default',
              engineerId,
              organizationId,
              useStudioAvailability: true, // Default to using studio availability
              isActive: true,
              unavailableDates: [],
            };
            return { data: { availability } };
          }

          const availability: EngineerAvailability = {
            id: availabilityDoc.id,
            ...convertTimestamps(availabilityDoc.data()),
          } as EngineerAvailability;

          return { data: { availability } };
        } catch (error: any) {
          console.error('Error fetching engineer availability:', error);
          return { error: { status: 'CUSTOM_ERROR', error: error.message } };
        }
      },
      providesTags: (result, error, { engineerId }) => [
        { type: 'EngineerAvailability', id: engineerId },
      ],
    }),

    /**
     * Create or update engineer availability settings
     */
    upsertEngineerAvailability: builder.mutation<EngineerAvailabilityResponse, CreateEngineerAvailabilityRequest>({
      async queryFn(availabilityData) {
        try {
          const { engineerId, organizationId, ...data } = availabilityData;

          // Use batch write for atomic update of both locations
          const batch = writeBatch(DB);

          // 1. Update detailed availability settings (subcollection)
          const availabilityRef = doc(
            DB,
            'organisers',
            organizationId,
            'engineers',
            engineerId,
            'availability_settings',
            'default'
          );

          const availabilityDoc = await getDoc(availabilityRef);
          const isUpdate = availabilityDoc.exists();

          const availabilityUpdate = {
            ...data,
            engineerId,
            organizationId,
            ...(isUpdate ? { updatedAt: serverTimestamp() } : { createdAt: serverTimestamp(), updatedAt: serverTimestamp() }),
          };

          batch.set(availabilityRef, availabilityUpdate, { merge: true });

          // 2. Update denormalized data in main engineer document (for fast booking queries)
          const engineerRef = doc(DB, 'organisers', organizationId, 'engineers', engineerId);
          batch.update(engineerRef, {
            quickAvailability: {
              isAvailable: data.isActive,
              useStudioAvailability: data.useStudioAvailability,
              workingHours: data.workingHours || null,
              unavailableDates: data.unavailableDates || [],
              lastUpdated: serverTimestamp(),
            },
            updatedAt: serverTimestamp(),
          });

          // Commit batch write
          await batch.commit();

          // Fetch updated availability document
          const updatedDoc = await getDoc(availabilityRef);
          const availability: EngineerAvailability = {
            id: updatedDoc.id,
            ...convertTimestamps(updatedDoc.data()),
          } as EngineerAvailability;

          return { data: { availability } };
        } catch (error: any) {
          console.error('Error upserting engineer availability:', error);
          return { error: { status: 'CUSTOM_ERROR', error: error.message } };
        }
      },
      invalidatesTags: (result, error, { engineerId }) => [
        { type: 'EngineerAvailability', id: engineerId },
        { type: 'Engineer', id: engineerId },
        'Engineers',
      ],
    }),

    /**
     * Add unavailable dates for an engineer
     */
    addEngineerUnavailableDates: builder.mutation<EngineerAvailabilityResponse, { organizationId: string; engineerId: string; dates: string[] }>({
      async queryFn({ organizationId, engineerId, dates }) {
        try {
          // Use batch write for atomic update
          const batch = writeBatch(DB);

          const availabilityRef = doc(
            DB,
            'organisers',
            organizationId,
            'engineers',
            engineerId,
            'availability_settings',
            'default'
          );

          const availabilityDoc = await getDoc(availabilityRef);
          const existingDates = availabilityDoc.exists()
            ? (availabilityDoc.data().unavailableDates || [])
            : [];

          // Merge and deduplicate dates
          const allDates = Array.from(new Set([...existingDates, ...dates])).sort();

          // 1. Update detailed availability settings
          batch.set(
            availabilityRef,
            {
              engineerId,
              organizationId,
              unavailableDates: allDates,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );

          // 2. Update denormalized data in main engineer document
          const engineerRef = doc(DB, 'organisers', organizationId, 'engineers', engineerId);
          batch.update(engineerRef, {
            'quickAvailability.unavailableDates': allDates,
            'quickAvailability.lastUpdated': serverTimestamp(),
            updatedAt: serverTimestamp(),
          });

          await batch.commit();

          const updatedDoc = await getDoc(availabilityRef);
          const availability: EngineerAvailability = {
            id: updatedDoc.id,
            ...convertTimestamps(updatedDoc.data()),
          } as EngineerAvailability;

          return { data: { availability } };
        } catch (error: any) {
          console.error('Error adding unavailable dates:', error);
          return { error: { status: 'CUSTOM_ERROR', error: error.message } };
        }
      },
      invalidatesTags: (result, error, { engineerId }) => [
        { type: 'EngineerAvailability', id: engineerId },
        { type: 'Engineer', id: engineerId },
        'Engineers',
      ],
    }),

    /**
     * Remove unavailable dates for an engineer
     */
    removeEngineerUnavailableDates: builder.mutation<EngineerAvailabilityResponse, { organizationId: string; engineerId: string; dates: string[] }>({
      async queryFn({ organizationId, engineerId, dates }) {
        try {
          const availabilityRef = doc(
            DB,
            'organisers',
            organizationId,
            'engineers',
            engineerId,
            'availability_settings',
            'default'
          );

          const availabilityDoc = await getDoc(availabilityRef);
          if (!availabilityDoc.exists()) {
            return { error: { status: 'NOT_FOUND', error: 'Availability settings not found' } };
          }

          const existingDates = availabilityDoc.data().unavailableDates || [];
          const updatedDates = existingDates.filter((date: string) => !dates.includes(date));

          // Use batch write for atomic update
          const batch = writeBatch(DB);

          // 1. Update detailed availability settings
          batch.update(availabilityRef, {
            unavailableDates: updatedDates,
            updatedAt: serverTimestamp(),
          });

          // 2. Update denormalized data in main engineer document
          const engineerRef = doc(DB, 'organisers', organizationId, 'engineers', engineerId);
          batch.update(engineerRef, {
            'quickAvailability.unavailableDates': updatedDates,
            'quickAvailability.lastUpdated': serverTimestamp(),
            updatedAt: serverTimestamp(),
          });

          await batch.commit();

          const updatedDoc = await getDoc(availabilityRef);
          const availability: EngineerAvailability = {
            id: updatedDoc.id,
            ...convertTimestamps(updatedDoc.data()),
          } as EngineerAvailability;

          return { data: { availability } };
        } catch (error: any) {
          console.error('Error removing unavailable dates:', error);
          return { error: { status: 'CUSTOM_ERROR', error: error.message } };
        }
      },
      invalidatesTags: (result, error, { engineerId }) => [
        { type: 'EngineerAvailability', id: engineerId },
        { type: 'Engineer', id: engineerId },
        'Engineers',
      ],
    }),

    // ==================== WEEKLY SCHEDULES ====================

    /**
     * Get a specific week schedule
     */
    getWeekSchedule: builder.query<{ weekSchedule: WeeklyScheduleDocument | null }, GetWeekScheduleRequest>({
      async queryFn({ organizationId, engineerId, weekStartDate }) {
        try {
          const weekRef = doc(
            DB,
            'organisers',
            organizationId,
            'engineers',
            engineerId,
            'availability_settings',
            weekStartDate
          );

          const weekDoc = await getDoc(weekRef);

          if (!weekDoc.exists()) {
            return { data: { weekSchedule: null } };
          }

          const weekSchedule: WeeklyScheduleDocument = {
            id: weekDoc.id,
            ...convertTimestamps(weekDoc.data()),
          } as WeeklyScheduleDocument;

          return { data: { weekSchedule } };
        } catch (error: any) {
          console.error('Error fetching week schedule:', error);
          return { error: { status: 'CUSTOM_ERROR', error: error.message } };
        }
      },
      providesTags: (result, error, { engineerId, weekStartDate }) => [
        { type: 'EngineerAvailability', id: `${engineerId}-${weekStartDate}` },
      ],
    }),

    /**
     * List all week schedules for an engineer
     */
    listWeekSchedules: builder.query<{ weekSchedules: WeeklyScheduleDocument[] }, ListWeekSchedulesRequest>({
      async queryFn({ organizationId, engineerId, startDate, endDate }) {
        try {
          const availabilityRef = collection(
            DB,
            'organisers',
            organizationId,
            'engineers',
            engineerId,
            'availability_settings'
          );

          // Build query - exclude 'default' document and apply date filters if provided
          let q = query(availabilityRef, where('__name__', '!=', 'default'));

          const snapshot = await getDocs(q);
          let weekSchedules: WeeklyScheduleDocument[] = snapshot.docs
            .map((doc) => ({
              id: doc.id,
              ...convertTimestamps(doc.data()),
            }))
            .filter((doc): doc is WeeklyScheduleDocument => {
              // Filter by date range if provided
              if (startDate && doc.weekStartDate < startDate) return false;
              if (endDate && doc.weekStartDate > endDate) return false;
              return true;
            });

          // Sort by week start date
          weekSchedules.sort((a, b) => a.weekStartDate.localeCompare(b.weekStartDate));

          return { data: { weekSchedules } };
        } catch (error: any) {
          console.error('Error listing week schedules:', error);
          return { error: { status: 'CUSTOM_ERROR', error: error.message } };
        }
      },
      providesTags: (result, error, { engineerId }) => [
        { type: 'EngineerAvailability', id: `${engineerId}-weeks` },
      ],
    }),

    /**
     * Create or update a week schedule
     */
    upsertWeekSchedule: builder.mutation<{ weekSchedule: WeeklyScheduleDocument }, UpsertWeekScheduleRequest>({
      async queryFn({ organizationId, engineerId, weekStartDate, schedule }) {
        try {
          const weekRef = doc(
            DB,
            'organisers',
            organizationId,
            'engineers',
            engineerId,
            'availability_settings',
            weekStartDate
          );

          const weekDoc = await getDoc(weekRef);
          const isUpdate = weekDoc.exists();

          const weekData = {
            id: weekStartDate,
            engineerId,
            organizationId,
            weekStartDate,
            weekEndDate: getWeekEndDate(weekStartDate),
            schedule,
            ...(isUpdate ? { updatedAt: serverTimestamp() } : { createdAt: serverTimestamp(), updatedAt: serverTimestamp() }),
          };

          await setDoc(weekRef, weekData, { merge: true });

          const updatedDoc = await getDoc(weekRef);
          const weekSchedule: WeeklyScheduleDocument = {
            id: updatedDoc.id,
            ...convertTimestamps(updatedDoc.data()),
          } as WeeklyScheduleDocument;

          return { data: { weekSchedule } };
        } catch (error: any) {
          console.error('Error upserting week schedule:', error);
          return { error: { status: 'CUSTOM_ERROR', error: error.message } };
        }
      },
      invalidatesTags: (result, error, { engineerId, weekStartDate }) => [
        { type: 'EngineerAvailability', id: `${engineerId}-${weekStartDate}` },
        { type: 'EngineerAvailability', id: `${engineerId}-weeks` },
      ],
    }),

    /**
     * Delete a week schedule
     */
    deleteWeekSchedule: builder.mutation<void, DeleteWeekScheduleRequest>({
      async queryFn({ organizationId, engineerId, weekStartDate }) {
        try {
          const weekRef = doc(
            DB,
            'organisers',
            organizationId,
            'engineers',
            engineerId,
            'availability_settings',
            weekStartDate
          );

          await deleteDoc(weekRef);

          return { data: undefined };
        } catch (error: any) {
          console.error('Error deleting week schedule:', error);
          return { error: { status: 'CUSTOM_ERROR', error: error.message } };
        }
      },
      invalidatesTags: (result, error, { engineerId, weekStartDate }) => [
        { type: 'EngineerAvailability', id: `${engineerId}-${weekStartDate}` },
        { type: 'EngineerAvailability', id: `${engineerId}-weeks` },
      ],
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetEngineersByOrganizationQuery,
  useGetEngineerQuery,
  useCreateEngineerMutation,
  useUpdateEngineerMutation,
  useDeleteEngineerMutation,
  useToggleEngineerStatusMutation,
  useToggleEngineerAvailabilityMutation,
  useGetEngineerAvailabilityQuery,
  useUpsertEngineerAvailabilityMutation,
  useAddEngineerUnavailableDatesMutation,
  useRemoveEngineerUnavailableDatesMutation,
  useGetWeekScheduleQuery,
  useListWeekSchedulesQuery,
  useUpsertWeekScheduleMutation,
  useDeleteWeekScheduleMutation,
} = engineersApi;

// Export reducer
export const engineersApiReducer = engineersApi.reducer;
export const engineersApiMiddleware = engineersApi.middleware;
